import {
  BeakerIcon,
  CloudArrowUpIcon,
  CodeBracketSquareIcon,
  CpuChipIcon,
  PaintBrushIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

const technologyGroups = [
  {
    title: 'Frontend Engineering',
    description: 'Modern interfaces, fast interactions, and maintainable component systems for web products.',
    icon: CodeBracketSquareIcon,
    tools: ['React', 'JavaScript', 'Tailwind CSS', 'Three.js', 'Responsive UI'],
  },
  {
    title: 'Backend & APIs',
    description: 'Reliable business logic, admin workflows, authentication, and API layers that are easy to extend.',
    icon: ServerStackIcon,
    tools: ['Python', 'Django', 'Django REST Framework', 'PostgreSQL', 'JWT Auth'],
  },
  {
    title: 'AI & Automation',
    description: 'Practical AI features that improve operations, support teams, and product experiences.',
    icon: CpuChipIcon,
    tools: ['LLM Integration', 'Chatbots', 'Data Analysis', 'Recommendation Flows', 'Workflow Automation'],
  },
  {
    title: 'Design Tools',
    description: 'Clear product direction, polished visuals, and design systems that support development.',
    icon: PaintBrushIcon,
    tools: ['Figma', 'UI/UX Design', 'Brand Systems', 'Prototyping', 'Design Handoff'],
  },
  {
    title: 'Cloud & Deployment',
    description: 'Production-ready deployment foundations for shipping, monitoring, and scaling applications.',
    icon: CloudArrowUpIcon,
    tools: ['Docker', 'Nginx', 'PostgreSQL Hosting', 'CI/CD Ready', 'Environment Config'],
  },
  {
    title: 'Quality & Delivery',
    description: 'A practical delivery workflow for keeping releases stable, reviewable, and user-focused.',
    icon: BeakerIcon,
    tools: ['API Testing', 'Code Reviews', 'Build Checks', 'Performance Passes', 'Documentation'],
  },
];

export default function TechnologiesSection() {
  return (
    <section className="relative py-24 transition-colors duration-300 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-base font-medium text-primary dark:text-secondary">Technologies we use</p>
          <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight text-gray-950 dark:text-white sm:text-5xl">
            A practical toolkit for building modern digital products.
          </h2>
          <p className="mt-5 text-lg leading-8 text-gray-600 dark:text-gray-400">
            We choose tools that help us ship clean interfaces, reliable APIs, useful AI features, and polished brand experiences.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {technologyGroups.map((group) => {
            const Icon = group.icon;

            return (
              <article
                key={group.title}
                className="group rounded-lg border border-gray-200 bg-white p-6 transition duration-200 hover:border-secondary/60 dark:border-white/10 dark:bg-white/5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary text-secondary">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-950 dark:text-white">{group.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">{group.description}</p>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {group.tools.map((tool) => (
                    <span
                      key={tool}
                      className="rounded-lg border border-primary/10 bg-primary/5 px-3 py-1 text-xs font-medium text-primary dark:border-white/10 dark:bg-white/5 dark:text-secondary"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
